import { MasterTranslation } from '@/constants';
import { useGlobalState } from '@/hooks';
import { EDD as ShipmentEDD } from '@/modules/pmt/components/EDD';
import { LoadingBar } from '@/modules/pmt/components/LoadingBar';
import { useShipmentInfo } from '@/modules/pmt/hooks/useShipmentInfo';
import {
  type IShipmentData,
  type ITrackingEventDateOptions,
  ShipmentSearchStatus,
  TrackingMilestone,
} from '@/modules/pmt/interfaces';
import { isEddAvailable, trackingEventDateModify } from '@/modules/pmt/utils';
import type { FC } from 'react';
import { DateTimeWrapper, Dot, MerchantName, Time, Title } from './ShipmentCardInfo.styles';

const parcelDateFormatOptions: ITrackingEventDateOptions = {
  showDate: true,
  showTime: true,
  fullDay: false,
  showDay: false,
  isFormatTimeUnderOneDay: true,
};

const LoadingShipmentInfo = () => {
  const { t } = useGlobalState();
  return (
    <div>
      <Title>{t(MasterTranslation.LookingParcel)}</Title>
      <LoadingBar width={30} />
    </div>
  );
};

export const MerchantNameWithDot: FC = ({ children }) => {
  return (
    <>
      <MerchantName>{children}</MerchantName>
      <Dot />
    </>
  );
};

interface IFoundShipmentProps {
  merchantName?: string;
  shipmentData: IShipmentData;
}

const FoundShipmentInfo = ({ shipmentData, merchantName }: IFoundShipmentProps) => {
  const { t } = useGlobalState();
  const lastEvent = shipmentData.parcel?.lastEvent;
  const showEdd = isEddAvailable(shipmentData);

  if (!lastEvent) return null;

  const importedDate = trackingEventDateModify(lastEvent.date, parcelDateFormatOptions, t);

  return (
    <div>
      <Title>{t(lastEvent.key ?? lastEvent.type)}</Title>
      <DateTimeWrapper>
        {merchantName && <MerchantNameWithDot>{merchantName}</MerchantNameWithDot>}
        {showEdd ? <ShipmentEDD shipmentData={shipmentData} /> : <Time>{importedDate}</Time>}
      </DateTimeWrapper>
    </div>
  );
};

interface ICommonShipmentInfoProps {
  title: string;
  merchantName?: string;
  shipmentData: IShipmentData;
}

// This component is used for the shipments that are:
// - Open shipments
// - Not found shipments
// - Pending SME/Email Sync shipments (shipment with shipment_status = pending or search_status = timeout)
const CommonShipmentInfo = ({ title, merchantName, shipmentData }: ICommonShipmentInfoProps) => {
  const { t } = useGlobalState();
  const shipmentImportedDate = shipmentData.shipmentImportedDate ?? new Date().toISOString();
  const importedDate = trackingEventDateModify(shipmentImportedDate, parcelDateFormatOptions, t);
  const showEdd = shipmentData.isEmailSyncShipment && isEddAvailable(shipmentData);

  return (
    <div>
      <Title>{title}</Title>
      <DateTimeWrapper>
        {merchantName && <MerchantNameWithDot>{merchantName}</MerchantNameWithDot>}
        {showEdd ? <ShipmentEDD shipmentData={shipmentData} /> : <Time>{importedDate}</Time>}
      </DateTimeWrapper>
    </div>
  );
};

interface IShipmentCardInfoProps {
  shipmentState: ShipmentSearchStatus;
  shipmentData?: IShipmentData;
}

const ShipmentCardInfo = ({ shipmentState, shipmentData }: IShipmentCardInfoProps) => {
  const { shipmentMerchantName } = useShipmentInfo(shipmentData);
  const { t } = useGlobalState();

  if (!shipmentData) return null;

  if (
    (shipmentData.isSMEShipment || shipmentData.isEmailSyncShipment) &&
    shipmentData.milestone === TrackingMilestone.Pending
  ) {
    return (
      <CommonShipmentInfo
        title={t(MasterTranslation.ToShip)}
        merchantName={shipmentMerchantName}
        shipmentData={shipmentData}
      />
    );
  }

  if (shipmentData.isOpenShipment) {
    return (
      <CommonShipmentInfo
        title={t(MasterTranslation.OrderConfirmed)}
        merchantName={shipmentMerchantName}
        shipmentData={shipmentData}
      />
    );
  }

  // either parcelId (trackingNumber) or orderId is present, or both are present
  switch (shipmentState) {
    case ShipmentSearchStatus.Timeout:
      return (
        <CommonShipmentInfo
          title={t(MasterTranslation.PendingParcel)}
          merchantName={shipmentMerchantName}
          shipmentData={shipmentData}
        />
      );

    case ShipmentSearchStatus.Pending:
    case ShipmentSearchStatus.ScrapingWithCarrier:
      return <LoadingShipmentInfo />;

    case ShipmentSearchStatus.Fetched:
      return <FoundShipmentInfo merchantName={shipmentMerchantName} shipmentData={shipmentData} />;

    default:
      return null;
  }
};

export { ShipmentCardInfo };
